import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { usePortalRole } from '../../contexts/PortalRoleContext';
import { listStudents, createStudent, updateStudent, deleteStudent, getGradeLabel, GRADES, STUDENT_STATUSES, GENDERS } from '../../api/studentPortalApi';
import { listCampuses } from '../../api/campusPortalApi';
import { listUsers } from '../../api/portalUserApi';
import StudentDetailModal from '../../components/StudentDetailModal/StudentDetailModal';
import ExcelUploadModal from '../../components/Portal/ExcelUploadModal';
import GradePromotionModal from '../../components/Portal/GradePromotionModal';
import { getTermEndAdjustCampuses } from '../../utils/termEndAdjust';
import * as S from './Management.styles';

const StudentManagement = () => {
  const auth = useAuth();
  const { currentRole, currentUserId, currentUserCampus, currentUserCampusIds } = usePortalRole();

  // Data states
  const [students, setStudents] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGrade, setFilterGrade] = useState('all');
  // Default to user's campus if they have one assigned
  const [filterCampus, setFilterCampus] = useState(currentUserCampus || 'all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modal states
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [showGradePromotion, setShowGradePromotion] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Create form states
  const [newStudent, setNewStudent] = useState({
    name: '',
    gender: '',
    schoolName: '',
    grade: '',
    status: 'active',
    campuses: [],
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    parentAddress: '',
    additionalNotes: ''
  });

  // Get user's Cognito groups
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalGroups = userGroups.filter(g => g.startsWith('portal_'));

  // Permission checks
  const isDeveloper = portalGroups.includes('portal_developer');
  const isSystemAdmin = portalGroups.includes('portal_system_admin');
  const isDirector = portalGroups.includes('portal_director');
  const isManager = portalGroups.includes('portal_manager');
  const isAdmin = portalGroups.includes('portal_admin');
  const canView = isDeveloper || isSystemAdmin || isDirector || isManager || isAdmin;
  const canEdit = canView;
  const canViewAllCampuses = isDeveloper || isSystemAdmin;

  // Grade +1: only campuses with the CampusPortal.termEndAdjust flag that this user runs
  const gradePromotionCampuses = getTermEndAdjustCampuses(campuses, {
    isSystemAdmin: isDeveloper || isSystemAdmin,
    userCampusIds: currentUserCampusIds
  });
  const canGradePromote = gradePromotionCampuses.length > 0 &&
    (isDeveloper || isSystemAdmin || isManager || isAdmin);

  // Update campus filter when currentUserCampus becomes available (async profile load)
  useEffect(() => {
    if (!canViewAllCampuses && currentUserCampus) {
      // For multi-campus users, default to 'all' (their campuses)
      if (currentUserCampusIds.length > 1) {
        setFilterCampus('all');
      } else {
        setFilterCampus(currentUserCampus);
      }
    }
  }, [currentUserCampus, currentUserCampusIds, canViewAllCampuses]);

  // Fetch data function wrapped in useCallback
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [studentsData, campusesData, usersData] = await Promise.all([
        listStudents(),
        listCampuses(),
        listUsers()
      ]);
      setStudents(studentsData);
      setCampuses(campusesData);
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching data:', err, { currentRole, currentUserId });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentRole, currentUserId]);

  // Fetch data on mount
  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [canView, fetchData]);

  // Filter students based on campus visibility first
  const visibleStudents = canViewAllCampuses
    ? students
    : students.filter(student => {
        const studentCampuses = student.campuses || [];
        if (currentUserCampusIds.length > 0) {
          return studentCampuses.some(c => currentUserCampusIds.includes(c));
        }
        return currentUserCampus && studentCampuses.includes(currentUserCampus);
      });

  // Filter students
  const filteredStudents = visibleStudents.filter(student => {
    // Search filter
    const matchesSearch =
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.parentName?.toLowerCase().includes(searchTerm.toLowerCase());

    // Grade filter
    const matchesGrade = filterGrade === 'all' || student.grade === parseInt(filterGrade);

    // Campus filter
    const matchesCampus = filterCampus === 'all' ||
      (student.campuses || []).includes(filterCampus);

    // Status filter
    const matchesStatus = filterStatus === 'all' || student.status === filterStatus;

    return matchesSearch && matchesGrade && matchesCampus && matchesStatus;
  });

  // Handle create student
  const handleCreateStudent = async () => {
    if (!newStudent.name.trim()) {
      alert('Please enter student name');
      return;
    }
    if (!newStudent.campuses || newStudent.campuses.length === 0) {
      alert('Please select at least one campus');
      return;
    }

    setActionLoading(true);
    try {
      const created = await createStudent({
        name: newStudent.name.trim(),
        gender: newStudent.gender || null,
        schoolName: newStudent.schoolName.trim(),
        grade: newStudent.grade ? parseInt(newStudent.grade) : null,
        status: newStudent.status,
        campuses: newStudent.campuses,
        parentName: newStudent.parentName.trim(),
        parentEmail: newStudent.parentEmail.trim(),
        parentPhone: newStudent.parentPhone.trim(),
        parentAddress: newStudent.parentAddress.trim(),
        additionalNotes: newStudent.additionalNotes.trim()
      });
      setStudents([...students, created]);
      setShowCreateModal(false);
      setNewStudent({
        name: '',
        gender: '',
        schoolName: '',
        grade: '',
        status: 'active',
        campuses: [],
        parentName: '',
        parentEmail: '',
        parentPhone: '',
        parentAddress: '',
        additionalNotes: ''
      });
      alert('Student created successfully!');
    } catch (err) {
      console.error('Error creating student:', err);
      alert(`Failed to create student: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle update student
  const handleSaveStudent = async (updatedStudent) => {
    setActionLoading(true);
    try {
      const updateFields = {
        name: updatedStudent.name,
        gender: updatedStudent.gender,
        schoolName: updatedStudent.schoolName,
        grade: updatedStudent.grade,
        status: updatedStudent.status,
        campuses: updatedStudent.campuses,
        parentName: updatedStudent.parentName,
        parentEmail: updatedStudent.parentEmail,
        parentPhone: updatedStudent.parentPhone,
        parentAddress: updatedStudent.parentAddress,
        additionalNotes: updatedStudent.additionalNotes
      };
      if (isDeveloper || isSystemAdmin) {
        updateFields.billingVisibleCampuses = updatedStudent.billingVisibleCampuses || [];
      }
      const updated = await updateStudent(updatedStudent.id, updateFields);
      setStudents(students.map(s => s.id === updated.id ? updated : s));
      setSelectedStudent(null);
      alert('Student updated successfully!');
    } catch (err) {
      console.error('Error updating student:', err);
      alert(`Failed to update student: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete student
  const handleDeleteStudent = async (studentId) => {
    setActionLoading(true);
    try {
      await deleteStudent(studentId);
      setStudents(students.filter(s => s.id !== studentId));
      setSelectedStudent(null);
      alert('Student deleted successfully!');
    } catch (err) {
      console.error('Error deleting student:', err);
      alert(`Failed to delete student: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle unlink parent from student
  const handleUnlinkParent = async (studentId) => {
    setActionLoading(true);
    try {
      const updated = await updateStudent(studentId, { parentId: '' });
      setStudents(students.map(s => s.id === updated.id ? updated : s));
      setSelectedStudent(updated);
      alert('Parent account unlinked successfully!\n家长账号已解除关联！');
    } catch (err) {
      console.error('Error unlinking parent:', err);
      alert(`Failed to unlink parent: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle link parent to student
  const handleLinkParent = async (studentId, parentUserId) => {
    setActionLoading(true);
    try {
      const updated = await updateStudent(studentId, { parentId: parentUserId });
      setStudents(students.map(s => s.id === updated.id ? updated : s));
      setSelectedStudent(updated);
      alert('Parent account linked successfully!\n家长账号已关联！');
    } catch (err) {
      console.error('Error linking parent:', err);
      alert(`Failed to link parent: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Get parent users filtered by campus overlap with student
  const getParentUsersForStudent = (student) => {
    if (!student) return [];
    const studentCampuses = student.campuses || [];
    return users.filter(user => {
      const hasParentRole = (user.roles || []).includes('portal_parent');
      if (!hasParentRole) return false;
      const userCampuses = user.campusIds || (user.campusId ? [user.campusId] : []);
      return userCampuses.some(cid => studentCampuses.includes(cid));
    });
  };

  // Handle batch create from Excel
  const handleBatchCreate = async (studentData) => {
    const created = await createStudent(studentData);
    setStudents(prev => [...prev, created]);
    return created;
  };

  // Handle campus multi-select for create form
  const toggleCampusSelection = (campusId) => {
    setNewStudent(prev => ({
      ...prev,
      campuses: prev.campuses.includes(campusId)
        ? prev.campuses.filter(id => id !== campusId)
        : [...prev.campuses, campusId]
    }));
  };

  // Get campus names for display
  const getCampusNames = (campusIds) => {
    if (!campusIds || campusIds.length === 0) return '-';
    return campusIds
      .map(id => campuses.find(c => c.id === id)?.name || '')
      .filter(Boolean)
      .join(', ');
  };

  // Show loading
  if (auth.isLoading || loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Student Management</S.Title>
            <S.Subtitle>Loading...</S.Subtitle>
          </div>
        </S.Header>
      </S.Container>
    );
  }

  // Show permission denied
  if (!canView) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Student Management</S.Title>
            <S.Subtitle>Access Restricted</S.Subtitle>
          </div>
        </S.Header>
        <S.ErrorBanner>
          You do not have permission to view students.
        </S.ErrorBanner>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>Student Management</S.Title>
          <S.Subtitle>Manage student information and enrollment</S.Subtitle>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: '12px' }}>
            {canGradePromote && (
              <S.Button color="#dc2626" onClick={() => setShowGradePromotion(true)}>
                Grade +1
              </S.Button>
            )}
            <S.Button color="#3b82f6" onClick={() => setShowExcelUploadModal(true)}>
              Upload Excel
            </S.Button>
            <S.Button color="#F1AF59" onClick={() => setShowCreateModal(true)}>
              + Add Student
            </S.Button>
          </div>
        )}
      </S.Header>

      {error && (
        <S.ErrorBanner>
          Error loading data: {error}
        </S.ErrorBanner>
      )}

      <S.FiltersBar>
        <S.SearchInput
          type="text"
          placeholder="Search by name, school, or parent..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <S.FilterSelect value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)}>
          <option value="all">All Grades</option>
          {GRADES.map(g => (
            <option key={g.value} value={g.value}>{g.label}</option>
          ))}
        </S.FilterSelect>
        <S.FilterSelect value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)}>
          <option value="all">{canViewAllCampuses ? 'All Campuses' : 'All My Campuses'}</option>
          {(canViewAllCampuses
            ? campuses
            : campuses.filter(c => currentUserCampusIds.includes(c.id))
          ).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </S.FilterSelect>
        <S.FilterSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          {STUDENT_STATUSES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </S.FilterSelect>
      </S.FiltersBar>

      <S.TableContainer>
        <S.Table $cards>
          <thead>
            <tr>
              <S.Th>Student Name</S.Th>
              <S.Th>Grade</S.Th>
              <S.Th>Campuses</S.Th>
              <S.Th>Parent</S.Th>
              <S.Th>Status</S.Th>
              <S.Th>Balance</S.Th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.length === 0 ? (
              <tr>
                <S.Td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>
                  {searchTerm || filterGrade !== 'all' || filterCampus !== 'all' || filterStatus !== 'all'
                    ? 'No students match your filters'
                    : 'No students found'}
                </S.Td>
              </tr>
            ) : (
              filteredStudents.map(student => (
                <tr
                  key={student.id}
                  onClick={() => setSelectedStudent(student)}
                  style={{ cursor: 'pointer' }}
                >
                  <S.Td data-label="Student">
                    <S.UserName>{student.name}</S.UserName>
                    {student.schoolName && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        {student.schoolName}
                      </div>
                    )}
                  </S.Td>
                  <S.Td data-label="Grade">{getGradeLabel(student.grade)}</S.Td>
                  <S.Td data-label="Campuses">{getCampusNames(student.campuses)}</S.Td>
                  <S.Td data-label="Parent">{student.parentName || '-'}</S.Td>
                  <S.Td data-label="Status">
                    <S.Badge color={student.status === 'active' ? '#10b981' : '#ef4444'}>
                      {student.status === 'active' ? 'Active' : 'Inactive'}
                    </S.Badge>
                  </S.Td>
                  <S.Td data-label="Balance">
                    {student.credit === null || student.credit === undefined ? (
                      <span style={{ color: '#9ca3af', fontWeight: 500 }}>--</span>
                    ) : (
                      <span style={{
                        fontWeight: 600,
                        color: student.credit >= 0 ? '#10b981' : '#ef4444'
                      }}>
                        ${Number(student.credit).toFixed(2)}
                      </span>
                    )}
                  </S.Td>
                </tr>
              ))
            )}
          </tbody>
        </S.Table>
      </S.TableContainer>

      <S.Stats>
        Total Students: {filteredStudents.length} |
        Active: {filteredStudents.filter(s => s.status === 'active').length} |
        Inactive: {filteredStudents.filter(s => s.status === 'inactive').length} |
        Total Balance: ${filteredStudents.reduce((sum, s) => sum + (s.credit || 0), 0).toFixed(2)}
      </S.Stats>

      {/* Create Student Modal */}
      {showCreateModal && (
        <S.ModalOverlay>
          <S.Modal style={{ maxWidth: '600px' }}>
            <S.ModalHeader>
              <h3>Add New Student</h3>
              <S.CloseButton onClick={() => setShowCreateModal(false)}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              <S.FormGroup>
                <label>Student Name *</label>
                <S.Input
                  type="text"
                  placeholder="Enter student name"
                  value={newStudent.name}
                  onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Gender</label>
                <S.FilterSelect
                  value={newStudent.gender}
                  onChange={(e) => setNewStudent({ ...newStudent, gender: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="">Select Gender</option>
                  {GENDERS.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </S.FilterSelect>
              </S.FormGroup>

              <S.FormGroup>
                <label>Grade</label>
                <S.FilterSelect
                  value={newStudent.grade}
                  onChange={(e) => setNewStudent({ ...newStudent, grade: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="">Select Grade</option>
                  {GRADES.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </S.FilterSelect>
              </S.FormGroup>

              <S.FormGroup>
                <label>School Name</label>
                <S.Input
                  type="text"
                  placeholder="Enter school name"
                  value={newStudent.schoolName}
                  onChange={(e) => setNewStudent({ ...newStudent, schoolName: e.target.value })}
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Campuses *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {campuses.map(campus => (
                    <S.Badge
                      key={campus.id}
                      color={newStudent.campuses.includes(campus.id) ? '#3b82f6' : '#e5e7eb'}
                      style={{
                        cursor: 'pointer',
                        color: newStudent.campuses.includes(campus.id) ? 'white' : '#374151'
                      }}
                      onClick={() => toggleCampusSelection(campus.id)}
                    >
                      {campus.name}
                    </S.Badge>
                  ))}
                </div>
              </S.FormGroup>

              <S.FormGroup>
                <label>Status</label>
                <S.FilterSelect
                  value={newStudent.status}
                  onChange={(e) => setNewStudent({ ...newStudent, status: e.target.value })}
                  style={{ width: '100%' }}
                >
                  {STUDENT_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </S.FilterSelect>
              </S.FormGroup>

              {/* Parent Information Section */}
              <div style={{ marginTop: '24px', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                <h4 style={{ margin: 0, fontSize: '16px', color: '#374151' }}>Parent Information</h4>
              </div>

              <S.FormGroup>
                <label>Parent Name</label>
                <S.Input
                  type="text"
                  placeholder="Enter parent name"
                  value={newStudent.parentName}
                  onChange={(e) => setNewStudent({ ...newStudent, parentName: e.target.value })}
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Parent Email</label>
                <S.Input
                  type="email"
                  placeholder="Enter parent email"
                  value={newStudent.parentEmail}
                  onChange={(e) => setNewStudent({ ...newStudent, parentEmail: e.target.value })}
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Parent Phone</label>
                <S.Input
                  type="tel"
                  placeholder="Enter parent phone"
                  value={newStudent.parentPhone}
                  onChange={(e) => setNewStudent({ ...newStudent, parentPhone: e.target.value })}
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Parent Address</label>
                <S.Input
                  type="text"
                  placeholder="Enter parent address"
                  value={newStudent.parentAddress}
                  onChange={(e) => setNewStudent({ ...newStudent, parentAddress: e.target.value })}
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Additional Notes</label>
                <S.TextArea
                  placeholder="Enter any additional notes..."
                  value={newStudent.additionalNotes}
                  onChange={(e) => setNewStudent({ ...newStudent, additionalNotes: e.target.value })}
                  rows={3}
                />
              </S.FormGroup>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={() => setShowCreateModal(false)}>
                Cancel
              </S.Button>
              <S.Button
                color="#F1AF59"
                onClick={handleCreateStudent}
                disabled={actionLoading}
              >
                {actionLoading ? 'Creating...' : 'Create Student'}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {/* Student Detail Modal */}
      {selectedStudent && (
        <StudentDetailModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          onSave={handleSaveStudent}
          onDelete={handleDeleteStudent}
          onUnlinkParent={handleUnlinkParent}
          onLinkParent={handleLinkParent}
          parentUsers={getParentUsersForStudent(selectedStudent)}
          canEdit={canEdit}
          campuses={campuses}
          canManageBillingVisibility={isDeveloper || isSystemAdmin}
        />
      )}

      {showGradePromotion && (
        <GradePromotionModal
          campuses={gradePromotionCampuses}
          onClose={() => setShowGradePromotion(false)}
          onDone={fetchData}
        />
      )}

      {/* Excel Upload Modal */}
      {showExcelUploadModal && (
        <ExcelUploadModal
          onClose={() => {
            setShowExcelUploadModal(false);
            fetchData(); // Refresh the list
          }}
          onCreate={handleBatchCreate}
          campuses={campuses}
        />
      )}
    </S.Container>
  );
};

export default StudentManagement;
